import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import Enums from "@/enums";
import { toast } from "react-toastify";
import Helpers from "@/Helpers";
import AdminUsersTable from "@elements/tables/AdminUsersTable";
import AdminUserCompaniesTable from "@elements/tables/AdminUserCompaniesTable";
import UserListTable from "@elements/tables/UserListTable";
import { DateTime } from "luxon";
import TestGrading from "@elements/modals/TestGrading";
import ImportUsersModal from "@elements/modals/importUsersModal";
import AssignUserTestsModal from "@elements/modals/AssignUserTestsModal";

import { TbTableImport } from "react-icons/tb";
import { LuFilePlus } from "react-icons/lu";
import { TbScoreboard } from "react-icons/tb";
import { IoTrashOutline } from "react-icons/io5";
import { MdCheckBox, MdCheckBoxOutlineBlank, MdCameraswitch } from "react-icons/md";
import { FaPlusSquare } from "react-icons/fa";
import { apiMessage } from "@/utils/apiMessage";

export default function CompanyUsersTab(props: any) {
  const { company, updateSpinner, activeTab, adminPerms } = props.tabData;
  const [userData, updateUserData] = useState<any>({
    sites: [],
    lastAccess: "",
  });
  const [showAssignUserTestsModal, setShowAssignUserTestsModal] = useState(false);
  const [isGradingModalVisible, setGradingModalVisible] = useState(false);
  const [gradingTestId, setGradingTestId] = useState<number | null>(null);
  const [userList, updateUserList] = useState<any[]>([]);
  const [activeUserId, updateActiveUserId] = useState<number | null>(null);
  const [activeUser, updateActiveUser] = useState<any>(null);
  
  function IsUserAdmin() {
    return userData.permissions && userData.permissions.includes(Enums.ROLE_PERMS.SystemAdmin);
  }

  const isAdminLike = adminPerms?.includes(Enums.ROLE_PERMS.CompanyAdmin)
    || adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin)
    || userData.permissions?.includes(Enums.ROLE_PERMS.CompanyAdmin)
    || userData.permissions?.includes(Enums.ROLE_PERMS.SystemAdmin);

  const canEditUsers = isAdminLike
    || adminPerms?.includes(Enums.ROLE_PERMS.EditUser)
    || userData.permissions?.includes(Enums.ROLE_PERMS.EditUser);

  const canAssignTests = isAdminLike
    || adminPerms?.includes(Enums.ROLE_PERMS.EditUser)
    || userData.permissions?.includes(Enums.ROLE_PERMS.EditUser);

  const canManageUserTests = isAdminLike
    || adminPerms?.includes(Enums.ROLE_PERMS.CreateUser)
    || adminPerms?.includes(Enums.ROLE_PERMS.EditUser)
    || userData.permissions?.includes(Enums.ROLE_PERMS.CreateUser)
    || userData.permissions?.includes(Enums.ROLE_PERMS.EditUser);

  useEffect(() => {
    if (isOnThisTab())
      getUserDataAsync();
  }, [activeTab]);

  useEffect(() => {
    if (activeUserId) {
      const user = userList.find((u) => u.userId === activeUserId)
      updateActiveUser(user);
      if (user && user.jobId) {
        const job = userData.companyJobs.find((j) => j.id === user.jobId);
        if (job && !user.title) {
          user.title = job.name;
        }
      }
    }
  }, [activeUserId]);

  if (!isOnThisTab()) return null;
  function isOnThisTab() {
    return activeTab && activeTab === "users";
  }


  async function getUserDataAsync(newSelectedUserId?: number | null) {
    if (company.id) {
      updateSpinner(true);
      const resp = await RightTimeApi.getAsync("/CompanyUsers/" + company.id);
      updateSpinner(false);
      if (resp && resp.ok) {
        let sortingIndex = [];
        updateUserData(resp.jsonData);

        if (Array.isArray(resp.jsonData.users) && resp.jsonData.users.length > 0) {
          sortingIndex = resp.jsonData.users.map(user => ({ ...user }));

          // Step 2: Sort this simple array
          sortingIndex.sort((a, b) => {
            if (a.firstName < b.firstName) return -1;
            if (a.firstName > b.firstName) return 1;

            if (a.lastName < b.lastName) return -1;
            if (a.lastName > b.lastName) return 1;

            return 0;
          });

          // Update the state with the sorted array
          updateUserList(sortingIndex);
          const selectedUserId = newSelectedUserId || activeUserId;
          if (selectedUserId && resp.jsonData.users.length > 0) {
            if (!resp.jsonData.users.find((u) => u.userId === selectedUserId)) {
              let u = resp.jsonData.users[0];
              updateActiveUserId(u.userId);
            }
            else {
              if (selectedUserId == activeUserId) {
                const localUser = userList.find((u) => u.userId === activeUserId)
                if (localUser) {
                  const supervisor = userList.find((u) => u.userId === localUser.supervisorId);
                  if (supervisor)
                    localUser.supervisorName = supervisor.firstName + " " + supervisor.lastName;
                  else
                    localUser.supervisorName = "";
                  updateActiveUser(localUser);
                }
              }
              else
                updateActiveUserId(selectedUserId);
            }
          }
          if(!selectedUserId && sortingIndex.length > 0) {
            // If no user is selected, set the first user as active
            updateActiveUserId(sortingIndex[0].userId);
          }
        }
      }
    }
  }

  function setActiveUser(e) {
    var id = parseInt(e.currentTarget.getAttribute("data-id"));
    updateActiveUserId(id);
  }

  async function toggleUserRoleAsync(uId, rId) {
    let data = {
      roleId: rId,
      userId: uId,
      companyId: company.id,
    };
    updateSpinner(true);
    const resp = await RightTimeApi.putAsync("/CompanyUserRoleToggle", data);
    updateSpinner(false);
    if (resp && resp.ok) {
      await getUserDataAsync();
      toast.success(
        "Role '" +
        resp.jsonData.roleName +
        "' " +
        (resp.jsonData.active ? "enabled" : "disabled") +
        " for " +
        resp.jsonData.userFirstName +
        " " +
        resp.jsonData.userLastName
      );
    } else {
      const failMsg = apiMessage(resp);
      if (failMsg)
        toast.error(failMsg);
    }
  }

  async function toggleAllUserRolesAsync() {
    if (!canEditUsers || !activeUserId || !company?.id) return;
    if (!Array.isArray(company?.roles) || company.roles.length === 0) return;

    const selectedRoleIds = new Set(
      (userData.userRoles || [])
        .filter((ur) => ur.userId === activeUserId)
        .map((ur) => ur.companyRoleId)
    );
    const totalRoles = company.roles.length;
    const checkedCount = company.roles.filter((r) => selectedRoleIds.has(r.key)).length;
    const shouldEnableAll = checkedCount <= totalRoles / 2;

    const rolesToToggle = company.roles.filter((r) =>
      shouldEnableAll ? !selectedRoleIds.has(r.key) : selectedRoleIds.has(r.key)
    );

    if (rolesToToggle.length === 0) return;

    updateSpinner(true);
    const results = await Promise.all(
      rolesToToggle.map((role) =>
        RightTimeApi.putAsync("/CompanyUserRoleToggle", {
          roleId: role.key,
          userId: activeUserId,
          companyId: company.id
        })
      )
    );
    updateSpinner(false);

    if (results.some((resp) => resp && resp.ok)) {
      toast.success(
        shouldEnableAll
          ? `Access granted for ${rolesToToggle.length} role(s)`
          : `Access removed for ${rolesToToggle.length} role(s)`
      );
      await getUserDataAsync();
    } else {
      toast.error("Error updating role access");
    }
  }

  async function sendInviteAsync(e) {
    let userId = parseInt(e.currentTarget.getAttribute("data-id"));
    updateSpinner(true);
    const resp = await RightTimeApi.postAsync("/SendInvite", {
      userId: userId,
    });
    updateSpinner(true);
    if (resp && resp.ok) {
      toast.success("Invite Sent");
    }
  }



  async function onAddTestAsync(e) {
    console.log("onAddTestAsync called with event:", e);
    console.log("activeUserId:", activeUserId, "activeUser:", activeUser);
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    if (activeUserId) {
      console.log("Setting test mode to existingUser");
      setShowAssignUserTestsModal(true);
    } else {
      console.log("No active user, showing warning");
      toast.warning("Please select a user first");
    }
  }
  async function deleteTestAsync(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    const apiData = { companyId: company.id, testIds: [id] };
    updateSpinner(true);
    const resp = await RightTimeApi.deleteAsync(`/Test/Assign`, apiData);
    updateSpinner(false);
    if (resp && resp.ok) {
      toast.success("Test disassociated from user");
      await getUserDataAsync();
    }
  }

  function onCloseAssignUserTestsModal() {
    setShowAssignUserTestsModal(false);
    getUserDataAsync();
  }

  function showGradingModal(e) {
    const testId = parseInt(e.currentTarget.getAttribute("data-id"), 10);
    if (testId) {
      setGradingTestId(testId); // Set the test data for the modal
      setGradingModalVisible(true); // Show the modal
    }
  }

  async function onUpdateUserPropertyAsync(e) {
    let value = e.target.value;
    let prop = e.currentTarget.getAttribute("data-prop");
    let type = e.currentTarget.getAttribute("data-type");
    switch (type) {
      case "int": value = parseInt(value); break;
    }
    let updatedUserList = userList.map((user) => {
      if (user.userId === activeUserId) {
        return {
          ...user,
          [prop]: value || "", // Ensure we never store null
        };
      }
      return user;
    });
    updateUserList(updatedUserList);
    updateActiveUser({ ...activeUser, [prop]: value || "" });
  }

  async function onSelectJobAsync(e) {
    let value = parseInt(e.target.value);
    updateSpinner(true);
    var resp = await RightTimeApi.putAsync(`/CompanyUser/${activeUserId}/Job/${value}`);
    updateSpinner(false);
    if (resp && resp.ok) {
      toast.success("Job updated");
      await getUserDataAsync();
    }
    else
      toast.error("Error updating job");
  }

  const modalButtonText = "Assign Tests";
  const modalTitleText = activeUser
    ? "Selected tests to assign to: " + activeUser.firstName + " " + activeUser.lastName
    : "Select tests to assign";


  return (
    <>
      <div className="companyUsersTab">
        <UserListTable
          userList={userList}
          userData={userData}
          activeUser={activeUser}
          activeUserId={activeUserId}
          updateActiveUserId={updateActiveUserId}
          onUserListUpdate={getUserDataAsync}
          companyId={company.id}
          tabData={props.tabData}
          adminPerms={adminPerms}
          className="companyUsersTable"
        />

        <div className="tableWrapper factTable horizontal scrollable userTestsTable">
          <table data-help-id="userTestsTable">
            <caption>
              <span className="title">User / Tests
              </span><span
                data-help-id="assignTestToUser"
                className={`captionRight addUserButton floatRight${canAssignTests ? "" : " disabled"}`}
                onClick={(e) => {
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  if (!canAssignTests) return;
                  if (activeUserId) {
                    setShowAssignUserTestsModal(true);
                  } else {
                    toast.warning("Please select a user first");
                  }
                }}
                title="Assign Tests to User"
              >
                <FaPlusSquare className="actionIcon plusIcon" />
              </span>
            </caption>
            <thead>
              <tr>
                <th>ID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Test</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {activeUser && activeUser.tests && activeUser.tests.map((ut) => {
                return (
                  <tr key={"test" + ut.testId}>
                    <td>{ut.testId}</td>
                    <td>{ut.firstName}</td>
                    <td>{ut.lastName}</td>
                    <td>{ut.companyTestTypeName}</td>
                    <td>{DateTime.fromISO(ut.testDate, { zone: "America/New_York" }).toFormat("MM/dd/yyyy")}</td>
                    <td className="actions">
                      {canManageUserTests ? (<>
                          <TbScoreboard
                            data-id={ut.testId}
                            className="actionIcon"
                            onClick={showGradingModal} />

                          <IoTrashOutline
                            className="actionIcon deleteIcon"
                            data-id={ut.testId}
                            onClick={deleteTestAsync}
                            title="Disassociate Test from user"
                          />
                        </>
                      ) : null}

                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="tableWrapper factTable horizontal UserRolesTable scrollable">
          <table className="formFields">
            <caption data-help-id="userRolesTable">
              <span className="title">User / Roles</span>
            </caption>
            <thead>
              <tr>
                <th>Role</th>
                <th
                  className="toggleColumnHeader"
                  onClick={toggleAllUserRolesAsync}
                  style={{ cursor: canEditUsers && activeUserId ? "pointer" : "default" }}
                  title={canEditUsers ? "Toggle access for all roles" : undefined}
                  data-help-id="toggleActive"
                >
                  <span className="toggleHeaderLabel">Access</span>
                  <span className="toggleHeaderIcon" aria-hidden="true">
                    <MdCameraswitch />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {activeUser && activeUserId && company.roles.map((r) => {
                return (
                  <tr key={"role" + r.key}>
                    {/* {(()=>{debugger})()} */}
                    <td>{r.value}</td>
                    <td
                      className="actions"
                      onClick={() => {
                        if (canEditUsers) toggleUserRoleAsync(activeUserId, r.key);
                      }}
                    >
                      {userData.userRoles.find(
                        (ur) =>
                          ur.userId === activeUserId &&
                          ur.companyRoleId === r.key
                      ) ? (
                        <MdCheckBox className={`checkbox checked actionIcon${canEditUsers ? "" : " disabled"}`} />
                      ) : (
                        <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditUsers ? "" : " disabled"}`} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {userData.companyId > 1 && userData.adminUsers.length > 0 && (
          <AdminUsersTable userData={userData} updateData={getUserDataAsync} adminPerms={adminPerms} />
        )}
        {activeUser && userData.companyId === 1 && (
          <AdminUserCompaniesTable userData={userData} updateData={getUserDataAsync} user={activeUser} adminPerms={adminPerms} />
        )}
      </div>


      <AssignUserTestsModal
        isOpen={showAssignUserTestsModal}
        onClose={onCloseAssignUserTestsModal}
        companyId={company.id}
        userId={activeUserId}
        mode="existingUser"
        caption={modalTitleText}
        buttonText={modalButtonText}
      />
      {isGradingModalVisible && (
        <TestGrading
          isOpen={isGradingModalVisible}
          onClose={() => setGradingModalVisible(false)} // Close the modal
          testId={gradingTestId} // Pass the test data to the modal
        />
      )}
    </>
  )
}


